import type { ReactNode } from 'react';
import { Pagination, Spinner, Table } from 'react-bootstrap';
import type { Row } from '../api/types';
import { useIsCompact } from './useMediaQuery';

export interface Column<T = Row> {
  key: string;
  header: string;
  /** server sort key; when set the header is clickable */
  sortKey?: string;
  render?: (row: T) => ReactNode;
  align?: 'start' | 'end' | 'center';
  className?: string;
  /**
   * Dropped from the table below 768px so a phone gets a handful of columns that fit instead of a
   * table that must be scrolled sideways to read at all. The full row is still one tap away in the
   * edit/view action, so nothing here is unreachable — just deferred.
   */
  hideOnMobile?: boolean;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey?: (row: T) => string | number;
  loading?: boolean;
  fetching?: boolean;
  sort?: string;
  dir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPage?: (p: number) => void;
  actions?: (row: T) => ReactNode;
  empty?: string;
  caption?: string;
}

export function DataTable<T extends Row = Row>({
  columns, rows, rowKey = (r) => r.id, loading, fetching, sort, dir, onSort, page = 1, pageSize = 20, total, onPage, actions, empty = 'No records found', caption,
}: Props<T>) {
  const compact = useIsCompact();
  const visible = compact ? columns.filter((c) => !c.hideOnMobile) : columns;
  const pages = total !== undefined ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const alignClass = (a?: string) => (a === 'end' ? 'text-end' : a === 'center' ? 'text-center' : '');

  return (
    <div className="position-relative">
      <div className="table-responsive">
        <Table hover size="sm" className="align-middle mb-2 mms-table" aria-busy={fetching || loading}>
          {caption && <caption className="visually-hidden">{caption}</caption>}
          <thead>
            <tr>
              {visible.map((c) => {
                const active = !!c.sortKey && c.sortKey === sort;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className={`${alignClass(c.align)} ${c.className ?? ''} ${c.sortKey ? 'sortable' : ''}`}
                    aria-sort={active ? (dir === 'desc' ? 'descending' : 'ascending') : undefined}
                  >
                    {c.sortKey && onSort ? (
                      <button type="button" className="btn btn-link p-0 text-reset text-decoration-none fw-semibold" onClick={() => onSort(c.sortKey!)}>
                        {c.header}
                        <i className={`bi ms-1 ${active ? (dir === 'desc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill') : 'bi-chevron-expand text-body-tertiary'}`} aria-hidden />
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              {actions && <th scope="col" className="text-end" style={{ width: 1 }}><span className="visually-hidden">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {loading && !rows ? (
              <tr>
                <td colSpan={visible.length + (actions ? 1 : 0)} className="text-center py-5">
                  <Spinner animation="border" size="sm" role="status" /> <span className="ms-2">Loading…</span>
                </td>
              </tr>
            ) : rows && rows.length === 0 ? (
              <tr>
                <td colSpan={visible.length + (actions ? 1 : 0)} className="text-center text-body-secondary py-5">
                  {empty}
                </td>
              </tr>
            ) : (
              rows?.map((row) => (
                <tr key={rowKey(row)}>
                  {visible.map((c) => (
                    <td key={c.key} className={`${alignClass(c.align)} ${c.className ?? ''}`}>
                      {c.render ? c.render(row) : (row[c.key] ?? '')}
                    </td>
                  ))}
                  {actions && <td className="text-end text-nowrap">{actions(row)}</td>}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {total !== undefined && onPage && (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 no-print">
          <small className="text-body-secondary">
            {total === 0 ? '0 records' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            {fetching && <Spinner animation="border" size="sm" className="ms-2" role="status" aria-label="Refreshing" />}
          </small>
          {pages > 1 && (
            <Pagination size="sm" className="mb-0">
              <Pagination.Prev disabled={page <= 1} onClick={() => onPage(page - 1)} />
              {pageWindow(page, pages).map((p, i) =>
                p === '…' ? <Pagination.Ellipsis key={`e${i}`} disabled /> : (
                  <Pagination.Item key={p} active={p === page} onClick={() => onPage(p as number)}>{p}</Pagination.Item>
                ),
              )}
              <Pagination.Next disabled={page >= pages} onClick={() => onPage(page + 1)} />
            </Pagination>
          )}
        </div>
      )}
    </div>
  );
}

/** [1, '…', 4, 5, 6, '…', 20] style window around the current page. */
export function pageWindow(page: number, pages: number): (number | '…')[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set([1, pages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pages));
  const sorted = [...set].sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1]! > 1) out.push('…');
    out.push(p);
  });
  return out;
}
